// Resolve the identity to DISPLAY as a wish's owner.
//
// A wish is always owned (in the data model) by a user — the caretaker. But when
// the wish is FOR a pet (`wish.pet` is present), we show the PET as the owner:
// its name + avatar/color, plus a small "pet" affordance. Every wish card that
// renders an owner should go through this helper so pet-aware rendering stays
// consistent across the Shop feed, group wish lists, and the pet profile.
//
// Returns a normalized shape the <Avatar> component and the cards can use:
//   { isPet, name, avatarUrl, avatarColor, seed, species, birthday, petId }
export function wishOwner(wish) {
    if (wish?.pet) {
        return {
            isPet: true,
            name: wish.pet.name || 'Pet',
            avatarUrl: wish.pet.avatar_url || null,
            avatarColor: wish.pet.avatar_color || null,
            seed: wish.pet.id,
            species: wish.pet.species || null,
            birthday: wish.pet.birthday || null,
            petId: wish.pet.id,
        };
    }
    return {
        isPet: false,
        name: wish?.user?.name || 'Someone',
        avatarUrl: wish?.user?.avatar_url || null,
        avatarColor: wish?.user?.avatar_color || null,
        seed: wish?.user_id,
        species: null,
        birthday: wish?.user?.birthday || null,
        petId: null,
    };
}

// A stable identity key for a wish's DISPLAY owner, for grouping and filtering.
//
// Must be NAMESPACED: pet #5 and user #5 are different recipients, so a bare id
// would silently merge them into one section/filter chip. Everything that buckets
// wishes by recipient — the per-owner sections on the group Wishes tab and the
// person filter chips — goes through this so they can't drift apart.
export function ownerKey(wish) {
    const owner = wishOwner(wish);
    return owner.isPet ? `pet:${owner.petId}` : `user:${wish?.user_id}`;
}

// The distinct recipients across a list of wishes, ready for a filter chip row:
// [{ key, owner }], current user first, then everyone else alphabetically.
// `currentUserId` may be omitted when there's no "you" to float to the top.
export function distinctOwners(wishes, currentUserId = null) {
    const seen = new Map();
    for (const wish of wishes || []) {
        const key = ownerKey(wish);
        if (!seen.has(key)) seen.set(key, { key, owner: wishOwner(wish), userId: wish?.user_id });
    }
    return [...seen.values()].sort((a, b) => {
        const aSelf = !a.owner.isPet && a.userId === currentUserId;
        const bSelf = !b.owner.isPet && b.userId === currentUserId;
        if (aSelf !== bSelf) return aSelf ? -1 : 1;
        return (a.owner.name || '').localeCompare(b.owner.name || '');
    });
}
